// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, Kpi, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import { fullTimestamp } from "../components/timeFormat";

// 문의 관리 — 공개 페이지(/adopt 도입 상담, /support 고객지원)에서 접수된 문의함.
// 외부인(비회원)의 연락처가 담기므로 system_admin 전용(백엔드 require_system_admin! +
// SCREEN_GUARDS "inquiries"). 좌: 목록, 우: 선택한 문의 상세 + 상태 전이/처리 메모.

const KIND_LABEL   = { adoption: { label: "도입 상담", color: "orange" }, support: { label: "고객지원", color: "sky" } };
const STATUS_LABEL = {
  // action: 상태 전이 버튼 문구 — 한국어 조사(로/으로)가 받침에 따라 달라 따로 둔다.
  new:         { label: "접수",   color: "rose",  action: "접수로 되돌리기"   },
  in_progress: { label: "처리중", color: "amber", action: "처리중으로 변경"   },
  done:        { label: "완료",   color: "green", action: "완료 처리"         },
};
const STATUS_ORDER = ["new", "in_progress", "done"];

function Inquiries() {
  const { user } = useCurrentUser();
  const [rows, setRows]       = React.useState([]);
  const [counts, setCounts]   = React.useState({ total:0, new:0, in_progress:0, done:0, adoption:0, support:0 });
  const [kind, setKind]       = React.useState("");
  const [status, setStatus]   = React.useState("");
  const [q, setQ]             = React.useState("");
  const [sel, setSel]         = React.useState(null);
  const [err, setErr]         = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kind) params.set("kind", kind);
      if (status) params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const r = await fetch(`/api/v1/inquiries?${params}`, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        setErr(r.status === 401 ? "로그인이 필요합니다." : (data?.message || apiErrorMessage(data, r.status)));
        setRows([]);
        return;
      }
      const data = await r.json();
      setErr(null);
      setRows(data.inquiries || []);
      setCounts(data.counts || counts);
      // 선택 중이던 문의가 목록에 남아 있으면 최신 값으로 교체(상태 갱신 반영).
      setSel(prev => prev ? (data.inquiries || []).find(x => x.id === prev.id) || null : null);
    } catch (e) {
      setErr("문의를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [kind, status, q]);

  React.useEffect(() => { load(); }, [kind, status]);
  // 검색어는 입력이 멈춘 뒤에만 조회.
  React.useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
  }, [q]);

  const navRole = user?.role === "system_admin" ? "platform" : "region";

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role={navRole} active="inquiries"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="문의 관리" sub="도입 상담·고객지원 문의 접수함"/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">문의함</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>
                  공개 페이지(도입 안내·고객지원)에서 접수된 문의입니다. 처리 상태를 바꾸면 감사 로그에 남습니다.
                </div>
              </div>
              <button className="btn btn-ghost btn-md" onClick={load} disabled={loading}>{loading ? "불러오는 중…" : "새로고침"}</button>
            </div>

            {err && (
              <div style={{padding:'12px 16px', background:'var(--rose-50)', color:'var(--rose-500)',
                           borderRadius:10, marginBottom:14, fontSize:13, display:'flex', alignItems:'center', gap:10}}>
                <span style={{fontWeight:700}}>접근 불가</span><span>{err}</span>
                <a href="/login" className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}}>로그인</a>
              </div>
            )}

            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:18}}>
              <Kpi label="전체 문의"       value={`${counts.total.toLocaleString()}건`}/>
              <Kpi label="미처리(접수)"    value={`${counts.new.toLocaleString()}건`}         color="var(--rose-500)"/>
              <Kpi label="처리중"          value={`${counts.in_progress.toLocaleString()}건`} color="var(--amber-500)"/>
              <Kpi label="미완료 도입 상담" value={`${counts.adoption.toLocaleString()}건`}    color="var(--orange-500)"/>
            </div>

            <div style={{display:'grid', gridTemplateColumns: sel ? '1.3fr 1fr' : '1fr', gap:16, alignItems:'start'}}>
              <div className="card" style={{overflow:'hidden'}}>
                <div style={{padding:'14px 22px', display:'flex', alignItems:'center', gap:10, borderBottom:'1px solid var(--border-soft)'}}>
                  <div className="searchbar" style={{flex:'0 0 240px'}}>{Ic.search}
                    <input className="input" placeholder="이름·연락처·내용" value={q} onChange={e=>setQ(e.target.value)}/>
                  </div>
                  <select className="select" value={kind} onChange={e=>setKind(e.target.value)} style={{width:140}}>
                    <option value="">전체 종류</option>
                    <option value="adoption">도입 상담</option>
                    <option value="support">고객지원</option>
                  </select>
                  <select className="select" value={status} onChange={e=>setStatus(e.target.value)} style={{width:130}}>
                    <option value="">전체 상태</option>
                    {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s].label}</option>)}
                  </select>
                </div>

                <table className="tbl">
                  <thead><tr>
                    <th style={{paddingLeft:22}}>문의자</th>
                    <th>종류 · 유형</th>
                    <th>연락처</th>
                    <th>접수일</th>
                    <th>상태</th>
                  </tr></thead>
                  <tbody>
                    {rows.map(i => {
                      const kb = KIND_LABEL[i.kind] || { label: i.kind, color: "gray" };
                      const sb = STATUS_LABEL[i.status] || { label: i.status, color: "gray" };
                      return (
                        <tr key={i.id} onClick={() => setSel(i)}
                            style={{cursor:'pointer', background: sel?.id === i.id ? 'var(--bg-sub)' : undefined}}>
                          <td style={{paddingLeft:22}}>
                            <div style={{fontWeight:700, fontSize:12.5}}>{i.name}</div>
                            <div className="muted" style={{fontSize:10.5}}>{i.organization_name || "소속 미기재"}</div>
                          </td>
                          <td>
                            <span className={"badge badge-" + kb.color} style={{whiteSpace:'nowrap'}}>{kb.label}</span>
                            <div className="muted" style={{fontSize:10.5, marginTop:3}}>{i.topic || "유형 미지정"}</div>
                          </td>
                          <td className="mono muted" style={{fontSize:11}}>
                            {i.phone || "—"}<br/>{i.email || ""}
                          </td>
                          <td className="mono muted" style={{fontSize:11}}>{fullTimestamp(i.created_at)}</td>
                          <td><span className={"badge badge-" + sb.color}>{sb.label}</span></td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && !err && (
                      <tr><td colSpan={5} style={{textAlign:'center', padding:'36px 0', color:'var(--ink-3)', fontSize:13}}>
                        {loading ? "불러오는 중…" : "표시할 문의가 없습니다."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {sel && <InquiryDetail inquiry={sel} onClose={() => setSel(null)} onSaved={load}/>}
            </div>

            <AppFooter/>
          </div>
        </div>
      </div>
    </div>
  );
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function InquiryDetail({ inquiry, onClose, onSaved }) {
  const [note, setNote] = React.useState(inquiry.handling_note || "");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr]   = React.useState(null);

  React.useEffect(() => { setNote(inquiry.handling_note || ""); setErr(null); }, [inquiry.id]);

  const save = async (nextStatus) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/v1/inquiries/${inquiry.id}`, {
        method: "PATCH", credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ inquiry: { status: nextStatus || inquiry.status, handling_note: note } }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(apiErrorMessage(data, r.status));
      }
      onSaved();
    } catch (e) {
      setErr(e.message || "저장에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const kb = KIND_LABEL[inquiry.kind] || { label: inquiry.kind, color: "gray" };
  return (
    <div className="card card-pad" style={{position:'sticky', top:0}}>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
        <span className={"badge badge-" + kb.color}>{kb.label}</span>
        <div style={{fontWeight:800, fontSize:16}}>{inquiry.name}</div>
        <button className="btn btn-ghost btn-sm" style={{marginLeft:'auto'}} onClick={onClose}>닫기</button>
      </div>

      <DetailRow label="문의 유형" value={inquiry.topic || "—"}/>
      <DetailRow label="소속" value={inquiry.organization_name || "—"}/>
      <DetailRow label="연락처" value={inquiry.phone || "—"}/>
      <DetailRow label="이메일" value={inquiry.email || "—"}/>
      <DetailRow label="접수일" value={fullTimestamp(inquiry.created_at)}/>
      {inquiry.handled_by && <DetailRow label="최근 처리" value={`${inquiry.handled_by} · ${fullTimestamp(inquiry.handled_at)}`}/>}

      <div style={{marginTop:14, padding:'14px 16px', background:'var(--bg-sub)', borderRadius:12, fontSize:13, lineHeight:1.8, color:'var(--ink-1)', whiteSpace:'pre-wrap'}}>
        {inquiry.message}
      </div>

      <div style={{marginTop:16}}>
        <div style={{fontSize:12, fontWeight:700, color:'var(--ink-2)', marginBottom:6}}>처리 메모</div>
        <textarea className="input" rows={3} value={note} onChange={e=>setNote(e.target.value)}
          placeholder="회신 내용, 담당 배정 등 처리 경과를 적어두세요."
          style={{resize:'vertical', lineHeight:1.6, padding:'10px 12px', height:'auto', width:'100%'}}/>
      </div>

      {err && <div style={{marginTop:10, padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

      <div style={{display:'flex', gap:8, marginTop:14, flexWrap:'wrap'}}>
        {STATUS_ORDER.filter(s => s !== inquiry.status).map(s => (
          <button key={s} className={s === "done" ? "btn btn-primary btn-md" : "btn btn-ghost btn-md"}
            disabled={busy} onClick={() => save(s)}>
            {STATUS_LABEL[s].action}
          </button>
        ))}
        <button className="btn btn-ghost btn-md" disabled={busy} onClick={() => save(null)}>메모만 저장</button>
      </div>

      <div style={{display:'flex', gap:12, marginTop:14, paddingTop:12, borderTop:'1px solid var(--border-soft)', fontSize:12}}>
        {inquiry.email && <a href={`mailto:${inquiry.email}`} style={{color:'var(--orange-600)', textDecoration:'none'}}>이메일 회신 →</a>}
        {inquiry.phone && <a href={`tel:${inquiry.phone.replace(/[^0-9+]/g,'')}`} style={{color:'var(--orange-600)', textDecoration:'none'}}>전화 걸기 →</a>}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{display:'flex', gap:12, padding:'7px 0', borderTop:'1px solid var(--border-soft)', fontSize:12.5}}>
      <span style={{flex:'0 0 74px', color:'var(--ink-3)'}}>{label}</span>
      <span style={{flex:1, minWidth:0, color:'var(--ink-1)', fontWeight:600, wordBreak:'break-all'}}>{value}</span>
    </div>
  );
}

export { Inquiries };
